import { logger } from '@adornis/base/logging.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Observable, of } from 'rxjs';
import {
  AdvertisingMaterial,
  ChancenBrief,
  EventTicket,
  Furnishing,
  LearningMaterial,
  LogistikProduct,
  LogistikProductSet,
  TravelCertificate,
  WorkMaterial,
  WorkshopPack,
} from '../LogistikProduct.js';
import { LogistikProductCategory } from '../enums/LogistikEnums.js';

const subscribeLogistikProductByProductCategory = registerSubscription({
  operationName: 'subscribeLogistikProductByProductCategory',
  type: () => [LogistikProduct],
  params: [{ name: 'category', type: () => String }],
  resolve:
    (category: string) =>
    <SelSet extends BaseQLSelectionSet<LogistikProduct>>(gqlFields: SelSet) =>
      (Object.values(LogistikProductCategory)
        .map(a => a as string)
        .includes(category)
        ? watchQuery<LogistikProduct>(LogistikProduct._collectionName, getMatch(category as LogistikProductCategory))
        : of(null)) as Observable<Array<NarrowedEntity<LogistikProduct, SelSet>> | null>,
});

function getMatch(category: LogistikProductCategory) {
  switch (category) {
    case LogistikProductCategory.LEARNING_MATERIAL:
      return { _class: LearningMaterial._class };
    case LogistikProductCategory.ADVERTISING_MATERIAL:
      return { _class: AdvertisingMaterial._class };
    case LogistikProductCategory.WORK_MATERIAL:
      return { _class: WorkMaterial._class };
    case LogistikProductCategory.TICKETS:
      return { _class: EventTicket._class };
    case LogistikProductCategory.FURNISHING:
      return { _class: Furnishing._class };
    case LogistikProductCategory.WORKSHOP_PACK:
      return { _class: WorkshopPack._class };
    case LogistikProductCategory.CHANCEN_BRIEF:
      return { _class: ChancenBrief._class };
    case LogistikProductCategory.INDIVIDUAL:
      logger.error({ err: new Error('How can a product be an individual order?') });
      return {};
    case LogistikProductCategory.TRAVEL_CERTIFICATE:
      return { _class: TravelCertificate._class };
    case LogistikProductCategory.SET:
      return { _class: LogistikProductSet._class };
    default:
      return {};
  }
}

export namespace LogistikProductAPI {
  export const subscribeByProductCategory = subscribeLogistikProductByProductCategory;
}
